import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { Link } from "react-router-dom";
import routes from "../../../../engine/routes";
import { MERGE_STATUS, fmtDate } from "../institutionMerge.constants";
import { mergeApi, errMsg } from "../institutionMerge.api";

const { Title, Text } = Typography;
const DEBOUNCE_MS = 400;
// the search is remembered between visits, like the other lists (Logs.list: `logsSearcher`)
const STORAGE_KEY = "mergeHistorySearcher";
const readSaved = () => {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return v && typeof v === "object" ? v : {};
  } catch (e) {
    return {};
  }
};

/** What a matched institution's merge is called: its target when known, its label otherwise. */
const mergeTitle = m =>
  (m.targetInstitutionId && m.targetInstitutionId.name) ||
  (m.decisions && m.decisions.targetName) ||
  m.label ||
  m._id;

/**
 * « Malévoz - Laurier (Abacus 1234) — fusionnée dans : Malévoz le 12.09.2026 » — every
 * institution the search matched, with each merge it belongs to, linking to the merge.
 */
function MatchedInstitutions({ matched, merges }) {
  if (!matched || !matched.length) return null;
  const byId = new Map((merges || []).map(m => [String(m._id), m]));
  return (
    <Card
      size="small"
      title={`Établissements trouvés — ${matched.length}`}
      style={{ marginBottom: 12 }}
    >
      <Space direction="vertical" size={4}>
        {matched.map(inst => {
          const ids =
            inst.mergeIds && inst.mergeIds.length ? inst.mergeIds : [];
          return (
            <div key={inst._id}>
              <Text strong>{inst.name}</Text>
              {inst.abacusId ? (
                <Text type="secondary"> (Abacus {inst.abacusId})</Text>
              ) : null}
              {ids.length ? (
                ids.map((id, i) => {
                  const m = byId.get(String(id));
                  // the search also matches the institution that RECEIVED a merge — say so instead of
                  // « fusionnée dans : <itself> »
                  const isTarget =
                    !!m &&
                    String(
                      (m.targetInstitutionId && m.targetInstitutionId._id) ||
                        m.targetInstitutionId ||
                        "",
                    ) === String(inst._id);
                  return (
                    <span key={id}>
                      <Text type="secondary">
                        {i === 0
                          ? isTarget
                            ? " — a reçu la fusion : "
                            : " — fusionnée dans : "
                          : " · "}
                      </Text>
                      <Link to={routes.institutions().mergeDetail(id).path}>
                        {!m
                          ? "voir la fusion"
                          : isTarget
                          ? m.label || mergeTitle(m)
                          : mergeTitle(m)}
                      </Link>
                      {m && (
                        <Text type="secondary">
                          {" "}
                          le {fmtDate(m.appliedAt || m.createdAt)}
                          {m.status &&
                          MERGE_STATUS[m.status] &&
                          ![
                            "applied",
                            "verified",
                            "validated",
                            "finalized",
                          ].includes(m.status)
                            ? ` (${MERGE_STATUS[m.status].label.toLowerCase()})`
                            : ""}
                        </Text>
                      )}
                    </span>
                  );
                })
              ) : (
                <Text type="secondary"> — aucune fusion enregistrée</Text>
              )}
            </div>
          );
        })}
      </Space>
    </Card>
  );
}

/** Every merge ever run, newest first. Server-side pagination; search by institution / Abacus / label. */
export function MergeHistoryList() {
  const [state, setState] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 25,
    loading: false,
    matchedInstitutions: [],
  });
  const saved = useRef(readSaved()).current;
  const [status, setStatus] = useState(saved.status || undefined);
  const [text, setText] = useState(saved.q || "");
  const [q, setQ] = useState((saved.q || "").trim());
  const timer = useRef(null);
  // only the latest request may land: a slow answer to an older search is ignored
  const reqSeq = useRef(0);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ q, status }));
    } catch (e) {
      /* storage unavailable: the search simply is not remembered */
    }
  }, [q, status]);
  const onText = v => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setQ(v.trim()), DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const load = useCallback(
    async (page = 1, limit = 25) => {
      const seq = ++reqSeq.current;
      setState(s => ({ ...s, loading: true }));
      try {
        const d = await mergeApi.history(page, limit, status, q || undefined);
        if (seq !== reqSeq.current) return;
        setState({
          matchedInstitutions: [],
          ...d,
          loading: false,
        });
      } catch (e) {
        if (seq !== reqSeq.current) return;
        notification.error({ message: "Erreur", description: errMsg(e) });
        setState(s => ({ ...s, loading: false }));
      }
    },
    [status, q],
  );
  useEffect(() => {
    load(1, state.limit); /* eslint-disable-next-line */
  }, [load]);

  const columns = [
    {
      title: "Fusion",
      dataIndex: "label",
      render: (l, m) => (
        <Link to={routes.institutions().mergeDetail(m._id).path}>
          {l || m._id}
        </Link>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      render: s => (
        <Tag color={MERGE_STATUS[s]?.color} style={{ whiteSpace: "nowrap" }}>
          {MERGE_STATUS[s]?.label || s}
        </Tag>
      ),
    },
    {
      title: "Sources",
      dataIndex: "sourceInstitutionIds",
      render: xs => <Text>{(xs || []).map(x => x.name || x).join(", ")}</Text>,
    },
    {
      title: "Cible",
      dataIndex: "targetInstitutionId",
      render: t =>
        t ? (
          <Link to={routes.institutions(t._id).detail.path}>{t.name}</Link>
        ) : (
          "—"
        ),
    },
    { title: "Créée", dataIndex: "createdAt", render: fmtDate },
    {
      title: "Appliquée",
      dataIndex: "appliedAt",
      render: (d, m) =>
        d ? (
          <span>
            {fmtDate(d)} <Text type="secondary">{m.appliedBy}</Text>
          </span>
        ) : (
          "—"
        ),
    },
    { title: "Finalisée", dataIndex: "finalizedAt", render: fmtDate },
  ];

  return (
    <div className="merge-page" style={{ padding: "24px 24px 96px" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Title level={2}>Historique des fusions</Title>
        <Space wrap>
          <Input.Search
            allowClear
            style={{ width: 420 }}
            placeholder="Rechercher un établissement fusionné, un n° Abacus, un libellé…"
            value={text}
            onChange={e => onText(e.target.value)}
            onSearch={v => {
              clearTimeout(timer.current);
              setQ((v || "").trim());
            }}
          />
          <Select
            allowClear
            placeholder="Statut"
            style={{ width: 180 }}
            value={status}
            onChange={setStatus}
            options={Object.entries(MERGE_STATUS).map(([k, v]) => ({
              value: k,
              label: v.label,
            }))}
          />
          <Link to={routes.institutions().merge.path}>
            <Button type="primary">Nouvelle fusion</Button>
          </Link>
        </Space>
      </Space>
      <MatchedInstitutions
        matched={state.matchedInstitutions}
        merges={state.items}
      />
      <Table
        rowKey="_id"
        loading={state.loading}
        columns={columns}
        dataSource={state.items}
        pagination={{
          current: state.page,
          pageSize: state.limit,
          total: state.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          showTotal: (t, r) => `${r[0]}-${r[1]} sur ${t}`,
          onChange: load,
        }}
      />
    </div>
  );
}
